
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useProfile, useUpdateProfile, AVAILABLE_LANGUAGES } from "@/services/profileService";
import { useAuth } from "@/contexts/AuthContext";

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (code: string) => Promise<void>;
  availableLanguages: typeof AVAILABLE_LANGUAGES;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>("fr");
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();

  useEffect(() => {
    if (profile) {
      setCurrentLanguage(profile.language || "fr");
      setIsLoading(false);
    } else if (!user) {
      setIsLoading(false);
    }
  }, [profile, user]);

  const setLanguage = async (code: string) => {
    if (!profile) return;
    
    setCurrentLanguage(code);
    await updateProfile.mutateAsync({
      id: profile.id,
      language: code
    });
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setLanguage,
        availableLanguages: AVAILABLE_LANGUAGES,
        isLoading
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
